import React, { useRef, useLayoutEffect } from "react";
import {
  Tabs,
  BottomNavigation,
  AppBar,
  Toolbar,
  Portal,
  AppBarProps,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  PrpNavigationProvider,
  usePrpNavigation,
} from "./PrpNavigationContext";

export const PrpNavigation = ({
  children,
  appBarProps,
}: {
  children: React.ReactNode;
  appBarProps?: AppBarProps;
}) => {
  return (
    <PrpNavigationProvider>
      <PrpNavigationInner appBarProps={appBarProps}>
        {children}
      </PrpNavigationInner>
    </PrpNavigationProvider>
  );
};

export const PrpNavigationInner = ({
  children,
  appBarProps,
}: {
  children: React.ReactNode;
  appBarProps?: AppBarProps;
}) => {
  const { isMobile, selected } = usePrpNavigation();
  const theme = useTheme();
  const firstSelectedIndex = selected.findIndex((item) => item);
  const firstSelected = firstSelectedIndex === -1 ? false : firstSelectedIndex;
  // Avoid passing `false` to Tabs value, which can cause DOM insertBefore errors in some cases
  const tabsValue = firstSelected === false ? 0 : firstSelected;
  const tabsRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const calculateMargin = () => {
      if (!tabsRef.current) return;

      // Find the flex container (MuiTabs-flexContainer)
      const flexContainer = tabsRef.current.querySelector('.MuiTabs-flexContainer');
      if (!flexContainer) return;

      const children = Array.from(flexContainer.children) as HTMLElement[];
      if (children.length < 2) return; // Need at least Home + 1 item

      const homeItem = children[0];
      const navItems = children.slice(1).filter((item) => !item.dataset.excludeFromCentering);

      const homeWidth = homeItem.offsetWidth;
      const totalNavWidth = navItems.reduce((acc, item) => acc + item.offsetWidth, 0);
      const windowWidth = window.innerWidth;

      // Formula: (Window Center) - (Home Width) - (Half of Nav Items Width)
      const marginLeft = (windowWidth / 2) - homeWidth - (totalNavWidth / 2);
      const safeMarginLeft = Math.max(0, marginLeft);

      // Apply margin to the first navigation item (index 1)
      const firstNavItem = children[1];
      if (firstNavItem) {
        firstNavItem.style.marginLeft = `${safeMarginLeft}px`;
      }
    };

    calculateMargin();
    window.addEventListener("resize", calculateMargin);
    
    // Recalculate on children change (mutation observer could be better but this covers most cases)
    const timeout = setTimeout(calculateMargin, 50);

    return () => {
      window.removeEventListener("resize", calculateMargin);
      clearTimeout(timeout);
    };
  }, [isMobile]); // Re-run when children change

  if (!isMobile) {
    return (
      <AppBar
        id="app-bar"
        position="fixed"
        sx={{ zIndex: theme.zIndex.drawer + 1 }}
        {...appBarProps}
      >
        <Toolbar
          sx={{ justifyContent: "space-between", width: "100vw" }}
          disableGutters
        >
          <Tabs
            ref={tabsRef}
            value={tabsValue}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{
              "& .MuiTab-root": {
                minWidth: 0,
                flex: 1,
                padding: "5px 20px",
                color: "white",
                opacity: 0.7,
                transition: "opacity 0.3s, background-color 0.3s",
                "&:hover": {
                  opacity: 1,
                  backgroundColor: theme.palette.action.hover,
                  cursor: "pointer",
                },
                "&.Mui-selected": {
                  opacity: 1,
                },
                whiteSpace: "nowrap",
              },
              "& .MuiTabs-indicator": {
                boxShadow: (theme) =>
                  `0px -10px 26px 2px ${theme.palette.secondary.main}`,
              },
            }}
          >
            {children}
          </Tabs>
        </Toolbar>
      </AppBar>
    );
  }

  return (
    <>
      <Portal>
        {/* spacer for the bottom navigation */}
        <Box sx={{ height: "100px" }} />
      </Portal>
      <Portal>
        <BottomNavigation
          value={firstSelected}
          showLabels={true}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar + 400,
            height: "70px",
            bgcolor: theme.palette.primary.light,
            borderTop: "1px solid",
            borderColor: "divider",
            cursor: "pointer",
            "& .MuiBottomNavigationAction-root": {
              flex: 1,
              color: theme.palette.text.secondary,
              transition: "color 0.3s, background-color 0.3s",
              "&.Mui-selected": {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              },
              "& .MuiBottomNavigationAction-label": {
                whiteSpace: "nowrap",
              },
            },
          }}
        >
          {children}
        </BottomNavigation>
      </Portal>
    </>
  );
};
